import Link from 'next/link';
import React from 'react';
import BlueButtonBig from '../BlueButtonBig';
import localizedRoute from '../../utils/localizedRoute';
import { useForm } from 'react-hook-form';
import Spinner from '../Spinner';
import FormInput from '../FormInput';
import { useTranslation } from 'react-i18next';
import FormErrorMessage from '../FormErrorMessage';

const LoginPresentational = ({
  onLogin,
  loading
}: {
  onLogin: (values: { email: string; password: string }) => void;
  loading: boolean;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      email: '',
      password: ''
    }
  });
  const { t } = useTranslation();

  return (
    <div className="bg-grey-custom flex flex-col items-center justify-center py-16 px-6">
      <h1 className="text-5xl font-sans font-bold mb-8">{t('YOUR_ACCOUNT')}</h1>
      <div className="bg-white overflow-hidden shadow rounded-lg w-full lg:w-1/2">
        <div className="py-10 px-8">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-3xl font-bold font-sans">{t('LOGIN')}</h3>
          </div>
          <form onSubmit={handleSubmit(onLogin)}>
            <div className="my-2">
              <FormInput
                label="Email"
                type="email"
                id="email"
                placeholder={t('MAIL_ADDRESS')}
                invalid={Boolean(errors.email)}
                {...register('email', {
                  required: t('REQUIRED_FIELD') as string,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
                    message: t('INVALID_EMAIL')
                  }
                })}
              />

              <FormErrorMessage errors={errors} name="email" />
            </div>

            <div className="my-2">
              <FormInput
                label="Password"
                type="password"
                id="password"
                placeholder={t('PASSWORD_PLACEHOLDER')}
                invalid={Boolean(errors.password)}
                {...register('password', {
                  required: t('REQUIRED_FIELD') as string,
                  minLength: {
                    value: 6,
                    message: t('PASSWORD_MIN_LENGTH')
                  }
                })}
              />

              <FormErrorMessage errors={errors} name="password" />
            </div>

            <div className="my-2 ml-3">
              <Link
                href={localizedRoute('/forgot')}
                className="text-sm text-gray-400 hover:text-gray-600"
              >
                {t('PASSWORD_FORGOT')}
              </Link>
            </div>

            <div className="mt-4">
              {loading ? <Spinner /> : <BlueButtonBig text={t('LOGIN')} />}
            </div>
          </form>
        </div>
      </div>

      <div className="bg-login bg-cover bg-center mt-4 overflow-hidden shadow rounded-lg w-full lg:w-1/2 text-white">
        <div className="py-10 px-8">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-3xl font-bold font-sans">
              {t('CREATE_NEW_ACCOUNT')}
            </h3>
          </div>
          <div className="my-4 text-sm font-light">
            <ul className="list-inside list-disc">
              <li>
                <span>{t('FASTER_CHECKOUT')}</span>
              </li>
              <li>
                <span>{t('EXCLUSIVE_OFFERS')}</span>
              </li>
              <li>
                <span>{t('TRACK_ORDERS')}</span>
              </li>
            </ul>
          </div>
          <div className="mt-10">
            <Link href={localizedRoute('/signup')} passHref>
              <BlueButtonBig text={t('CREATE_ACCOUNT')} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPresentational;
